<template>
  <v-dialog v-model="showDialog" max-width="700px" @update:model-value="onDialogClose">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t.imsi?.moveToSubclient || 'Move IMSI to Subclient' }}</span>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3"></v-progress-linear>

        <!-- IMSI Info Display -->
        <v-card variant="outlined" class="mb-4 pa-3">
          <div class="text-subtitle-2 mb-2">IMSI Info</div>
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">IMSI:</span>
                <span class="detail-value">{{ simDetails.imsi || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">Serial:</span>
                <span class="detail-value">{{ simDetails.serial || '-' }}</span>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">{{ t.imsi?.currentOwner || 'Current Owner' }}:</span>
                <span class="detail-value">{{ simDetails.client_name || '-' }}</span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="detail-item">
                <span class="detail-label">IMSI Type:</span>
                <span class="detail-value">{{ simDetails.imsi_type_name || '-' }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <v-divider class="my-3"></v-divider>

        <!-- Target Client Selection (hierarchical tree) -->
        <ClientHierarchySelect
          v-model="selectedClientId"
          :clients-list="subclientsList"
          :label="t.imsi?.selectTargetSubclient || 'Select Target Subclient'"
          placeholder="Selecteaza client..."
          :disabled="loadingSubclients || submitting"
          @change="onClientChange"
        />

        <!-- Property Type Selection (Buy/Rent) -->
        <div v-if="hasPricingOptions" class="mt-4">
          <div class="text-subtitle-2 mb-2">Tip Proprietate</div>
          <v-radio-group v-model="propertyType" hide-details>
            <v-radio value="buy" :disabled="!hasBuyOption">
              <template v-slot:label>
                <span>
                  <strong>Cumparare</strong> (Plata unica)
                  <span v-if="hasBuyOption"> - {{ buyPriceOriginal }} {{ buyCurrencyOriginal }}</span>
                  <span v-else class="text-grey"> - Indisponibil</span>
                </span>
              </template>
            </v-radio>
            <v-radio value="rent" :disabled="!hasRentOptions">
              <template v-slot:label>
                <span>
                  <strong>Inchiriere</strong> (Recurent)
                  <span v-if="!hasRentOptions" class="text-grey"> - Indisponibil</span>
                </span>
              </template>
            </v-radio>
          </v-radio-group>
        </div>

        <!-- Rental Period Selection (if rent) -->
        <div v-if="propertyType === 'rent' && hasRentOptions" class="mt-3">
          <v-select
            v-model="selectedRentOption"
            :items="rentOptions"
            :item-title="formatRentOption"
            item-value="imsi_price_id"
            return-object
            label="Perioada de inchiriere"
            variant="outlined"
            density="compact"
          ></v-select>
        </div>

        <v-divider class="my-3"></v-divider>

        <!-- Delivery Section -->
        <div v-if="deliveryTypes.length > 0">
          <!-- Warning when no client selected -->
          <v-alert
            v-if="!selectedClientId"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ t.delivery?.selectClientFirst || 'Selecteaza un client pentru a configura livrarea' }}
          </v-alert>

          <DeliveryAddressSelector
            v-if="selectedClientId"
            :delivery-types="deliveryTypes"
            :show-toggle="true"
            :client-id="selectedClientId"
            @delivery-changed="onDeliveryChanged"
            @manageAddresses="goToManageAddresses"
          />
        </div>

        <v-divider v-if="hasPricingOptions && propertyType" class="my-3"></v-divider>

        <!-- Cost Breakdown - only show when property type is selected -->
        <OrderCostBreakdown
          v-if="hasPricingOptions && propertyType && costBreakdownLines.length > 0"
          :cost-lines="costBreakdownLines"
          :currency="currency"
          :total-amount="totalCost"
        >
          <template #payment-info>
            <v-alert
              v-if="totalCost === 0"
              type="success"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              <v-icon start size="small">mdi-check-circle</v-icon>
              Cost zero - IMSI-ul va fi mutat direct fara plata
            </v-alert>
            <v-alert
              v-else-if="targetClientIsPrepaid"
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              <v-icon start size="small">mdi-credit-card</v-icon>
              Clientul este prepaid - se va deschide formularul de plata
            </v-alert>
            <v-alert
              v-else
              type="success"
              variant="tonal"
              density="compact"
              class="mt-3"
            >
              <v-icon start size="small">mdi-file-document</v-icon>
              Costul va fi adaugat pe factura lunara
            </v-alert>
          </template>
        </OrderCostBreakdown>

        <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="successMessage" type="success" density="compact" class="mt-3">
          {{ successMessage }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="outlined" @click="closeDialog" :disabled="submitting">
          {{ t.common?.cancel || 'Cancel' }}
        </v-btn>
        <v-btn
          color="primary"
          @click="submitMove"
          :loading="submitting"
          :disabled="!canSubmit"
        >
          <v-icon start>mdi-check</v-icon>
          {{ (targetClientIsPrepaid && totalCost > 0) ? 'Continua la Plata' : (t.imsi?.move || 'Move') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getClientTree } from "@/composables/useGetClientsAPI";
import { getImsiMoveOptions, moveImsiToSubclient } from "@/composables/useGetAllocatedImsisAPI";
import { getCurrentEuroRonRate } from "@/composables/useEuroRonRateAPI";
import DeliveryAddressSelector from "@/components/DeliveryAddressSelector.vue";
import ClientHierarchySelect from "@/components/order/ClientHierarchySelect.vue";
import OrderCostBreakdown from "@/components/order/OrderCostBreakdown.vue";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'MoveImsiDialog',
  components: {
    DeliveryAddressSelector,
    ClientHierarchySelect,
    OrderCostBreakdown
  },
  props: {
    simId: {
      type: [Number, String],
      default: null
    },
    simDetails: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'moved'],
  data() {
    return {
      showDialog: true,
      loading: false,
      loadingSubclients: false,
      submitting: false,
      subclientsList: [],
      selectedClientId: null,
      selectedClientData: null,

      // Pricing options
      pricingOptions: { buy: null, rent: [] },
      propertyType: null, // 'buy' or 'rent'
      selectedRentOption: null,
      originalCurrency: 'EUR',

      // Exchange rate for EUR to RON conversion
      euroRonRate: 5.0, // Default fallback

      // Delivery
      deliveryTypes: [],
      deliveryData: {},

      // Messages
      errorMessage: null,
      successMessage: null
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    hasPricingOptions() {
      return this.hasBuyOption || this.hasRentOptions;
    },
    hasBuyOption() {
      return this.pricingOptions.buy != null;
    },
    hasRentOptions() {
      return this.pricingOptions.rent && this.pricingOptions.rent.length > 0;
    },
    // Display currency is always RON (all amounts converted)
    currency() {
      return 'RON';
    },
    buyPrice() {
      const price = this.pricingOptions.buy?.price || 0;
      const currency = this.pricingOptions.buy?.currency || this.originalCurrency;
      return this.convertToRon(price, currency);
    },
    buyPriceDisplay() {
      return this.buyPrice.toFixed(2);
    },
    // Original price without conversion (for display)
    buyPriceOriginal() {
      const price = parseFloat(this.pricingOptions.buy?.price) || 0;
      return price.toFixed(2);
    },
    buyCurrencyOriginal() {
      return this.pricingOptions.buy?.currency || this.originalCurrency;
    },
    rentOptions() {
      return this.pricingOptions.rent || [];
    },
    imsiCost() {
      if (this.propertyType === 'buy' && this.pricingOptions.buy) {
        const price = parseFloat(this.pricingOptions.buy.price) || 0;
        const currency = this.pricingOptions.buy.currency || this.originalCurrency;
        return this.convertToRon(price, currency);
      }
      if (this.propertyType === 'rent' && this.selectedRentOption) {
        const price = parseFloat(this.selectedRentOption.price) || 0;
        const currency = this.selectedRentOption.currency || this.originalCurrency;
        return this.convertToRon(price, currency);
      }
      return 0;
    },
    needsDelivery() {
      return this.deliveryData?.needDelivery === true;
    },
    hasDeliveryTypeSelected() {
      return this.needsDelivery && this.deliveryData?.deliveryTypeId != null;
    },
    deliveryCost() {
      // Only return delivery cost if user explicitly needs delivery AND has selected a type
      if (this.needsDelivery && this.hasDeliveryTypeSelected) {
        const cost = this.deliveryData?.deliveryCost || 0;
        const currency = this.deliveryData?.deliveryCurrency || 'RON';
        return this.convertToRon(cost, currency);
      }
      return 0;
    },
    totalCost() {
      return this.imsiCost + this.deliveryCost;
    },
    targetClientIsPrepaid() {
      // Check if selected client is prepaid
      if (this.selectedClientData) {
        return this.selectedClientData.is_prepaid === true ||
               this.selectedClientData.client_type === 'prepaid' ||
               this.selectedClientData.payment_type === 'prepaid';
      }
      return false;
    },
    costBreakdownLines() {
      const lines = [];
      if (this.propertyType === 'buy' && this.pricingOptions.buy) {
        const origPrice = parseFloat(this.pricingOptions.buy.price) || 0;
        const origCurrency = this.pricingOptions.buy.currency || this.originalCurrency;
        lines.push({
          label: `IMSI (Cumparare) - ${origPrice.toFixed(2)} ${origCurrency}`,
          amount: this.imsiCost
        });
      } else if (this.propertyType === 'rent' && this.selectedRentOption) {
        const origPrice = parseFloat(this.selectedRentOption.price) || 0;
        const origCurrency = this.selectedRentOption.currency || this.originalCurrency;
        lines.push({
          label: `IMSI (${this.selectedRentOption.billing_period_name || 'Inchiriere'}) - ${origPrice.toFixed(2)} ${origCurrency}`,
          amount: this.imsiCost
        });
      }
      // Only show delivery cost if user needs delivery AND has selected a delivery type
      if (this.needsDelivery && this.hasDeliveryTypeSelected && this.deliveryCost > 0) {
        const origDeliveryCost = parseFloat(this.deliveryData?.deliveryCost) || 0;
        const origDeliveryCurrency = this.deliveryData?.deliveryCurrency || 'RON';
        lines.push({
          label: `Livrare - ${origDeliveryCost.toFixed(2)} ${origDeliveryCurrency}`,
          amount: this.deliveryCost
        });
      }
      return lines;
    },
    canSubmit() {
      if (!this.selectedClientId) return false;
      if (this.hasPricingOptions) {
        if (!this.propertyType) return false;
        if (this.propertyType === 'rent' && !this.selectedRentOption) return false;
      }
      return true;
    }
  },
  methods: {
    async loadSubclients() {
      this.loadingSubclients = true;
      try {
        const token = store.getters.getToken;
        const clientIdForTree = this.simDetails.client_id || store.getters.getUser?.client_id;
        const response = await getClientTree(token, clientIdForTree);

        if (!response.error && response.clients_list) {
          this.subclientsList = this.buildSubclientsList(response.clients_list);
          log("Loaded subclients:");
          log(this.subclientsList);

          if (response.delivery_types) {
            this.deliveryTypes = response.delivery_types;
          }
        } else {
          log("Error loading subclients: " + (response.message || 'Unknown error'));
        }
      } catch (err) {
        log("Error loading subclients:");
        log(err);
        this.errorMessage = "Error loading subclients";
      } finally {
        this.loadingSubclients = false;
      }
    },

    async loadPricingOptions() {
      this.loading = true;
      try {
        const token = store.getters.getToken;
        const imsiId = this.simDetails.imsi_id || this.simId;

        if (!imsiId) {
          log("No imsi_id available for pricing options");
          this.loading = false;
          return;
        }

        const response = await getImsiMoveOptions(token, imsiId);

        if (!response.error && response.pricing_options) {
          this.pricingOptions = response.pricing_options || { buy: null, rent: [] };
          this.originalCurrency = response.currency || 'EUR';

          if (response.delivery_types && response.delivery_types.length > 0) {
            this.deliveryTypes = response.delivery_types;
          }

          // Auto-select first available option
          if (this.hasBuyOption) {
            this.propertyType = 'buy';
          } else if (this.hasRentOptions) {
            this.propertyType = 'rent';
            this.selectedRentOption = this.rentOptions[0];
          }

          log("Loaded pricing options:");
          log(this.pricingOptions);
        } else {
          // API nu exista sau nu returneaza pricing - continuam fara pricing
          log("No pricing options available (API may not exist): " + (response.message || ''));
          this.pricingOptions = { buy: null, rent: [] };
        }
      } catch (err) {
        // API nu exista - continuam fara pricing
        log("Error loading pricing options (API may not exist):");
        log(err);
        this.pricingOptions = { buy: null, rent: [] };
      } finally {
        this.loading = false;
      }
    },

    buildSubclientsList(clientsTree) {
      const list = [];
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return;
        clients.forEach(client => {
          const indent = level > 0 ? '\u2014'.repeat(level) + ' ' : '';
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level,
            is_prepaid: client.is_prepaid,
            client_type: client.client_type,
            payment_type: client.payment_type
          });
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1);
          }
        });
      };
      addClientsRecursively(clientsTree);
      return list;
    },

    onClientChange(data) {
      log("Client changed:", data);
      this.selectedClientId = data.clientId;
      this.selectedClientData = data.client;
    },

    onDeliveryChanged(data) {
      this.deliveryData = data;
      log("Delivery data changed:");
      log(data);
    },

    formatRentOption(item) {
      if (!item) return '';
      const price = parseFloat(item.price) || 0;
      const currency = item.currency || this.originalCurrency;
      // Show original price and currency (not converted)
      return `${item.billing_period_name || 'Period'} - ${price.toFixed(2)} ${currency}`;
    },

    // Convert amount from EUR to RON (rounded to 2 decimal places)
    convertToRon(amount, currency) {
      if (!amount) return 0;
      const numAmount = parseFloat(amount) || 0;
      if (currency === 'RON' || currency === 'Lei') {
        return Math.round(numAmount * 100) / 100;
      }
      // Assume EUR if not RON
      const converted = numAmount * this.euroRonRate;
      return Math.round(converted * 100) / 100;
    },

    // Load current EUR/RON exchange rate
    async loadExchangeRate() {
      try {
        const token = store.getters.getToken;
        const response = await getCurrentEuroRonRate(token);
        if (!response.error && response.rate) {
          this.euroRonRate = parseFloat(response.rate.euro_ron_rate) || 5.0;
          log("Loaded EUR/RON rate: " + this.euroRonRate);
        }
      } catch (err) {
        log("Error loading exchange rate, using default:");
        log(err);
      }
    },

    goToManageAddresses() {
      if (this.selectedClientId) {
        this.$router.push({ name: 'client', query: { id: this.selectedClientId } });
        this.closeDialog();
      }
    },

    async submitMove() {
      if (!this.canSubmit) {
        this.errorMessage = "Completeaza toate campurile obligatorii";
        return;
      }

      this.submitting = true;
      this.errorMessage = null;

      try {
        const token = store.getters.getToken;

        // Determine imsi_price_id based on selection
        let imsiPriceId = null;
        let billingPeriodId = null;
        if (this.propertyType === 'buy' && this.pricingOptions.buy) {
          imsiPriceId = this.pricingOptions.buy.imsi_price_id;
        } else if (this.propertyType === 'rent' && this.selectedRentOption) {
          imsiPriceId = this.selectedRentOption.imsi_price_id;
          billingPeriodId = this.selectedRentOption.billing_period_id;
        }

        const data = {
          target_client_id: this.selectedClientId,
          property_type: this.propertyType,
          imsi_price_id: imsiPriceId,
          billing_period_id: billingPeriodId,
          need_delivery: this.deliveryData?.needDelivery || false,
          delivery_type_id: this.deliveryData?.deliveryTypeId,
          delivery_details: this.deliveryData?.deliveryDetails,
          euro_ron_rate: this.euroRonRate,
          skip_payment: this.totalCost === 0
        };

        // For allocated IMSIs, use sim_id; for non-allocated, use imsi_id
        if (this.simId && this.simDetails.is_allocated !== false) {
          data.sim_id = this.simId;
        } else if (this.simDetails.imsi_id) {
          data.imsi_id = this.simDetails.imsi_id;
        } else {
          this.errorMessage = "No valid IMSI identifier found";
          this.submitting = false;
          return;
        }

        log("Submitting move:");
        log(data);

        const response = await moveImsiToSubclient(token, data);

        if (!response.error) {
          // Check if payment is needed (prepaid client)
          if (response.bt_form_url) {
            // Open payment form
            window.open(response.bt_form_url, '_blank');
            this.successMessage = response.message || "Redirectare la plata...";
          } else {
            this.successMessage = response.message || "IMSI mutat cu succes!";
          }

          this.$emit('moved', response);

          setTimeout(() => {
            this.closeDialog();
          }, 2000);
        } else {
          this.errorMessage = response.message || "Eroare la mutarea IMSI";
        }
      } catch (err) {
        log("Exception moving IMSI:");
        log(err);
        this.errorMessage = "Eroare la mutarea IMSI";
      } finally {
        this.submitting = false;
      }
    },

    closeDialog() {
      this.showDialog = false;
      this.$emit('close');
    },

    onDialogClose(value) {
      if (!value) this.$emit('close');
    }
  },

  async mounted() {
    await this.loadExchangeRate();
    this.loadSubclients();
    this.loadPricingOptions();
  }
});
</script>

<style scoped>
.detail-item {
  margin-bottom: 4px;
}
.detail-label {
  font-weight: 500;
  color: #666;
  display: block;
  font-size: 0.85rem;
}
.detail-value {
  font-size: 0.95rem;
}
</style>
